import { css } from '@linaria/atomic';
import { Trans, useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { CheckboxField, Text, useControlledField } from '@frontend/design-system';
import { TreeLevelStyle, TreeNode } from './tree-checklist.types';
import TreeConnector from './tree-connector.svg?react';
import { getNodeState, getSelectableLeafIds } from './tree-selection.utils';
import { WidthVariant } from './use-field-width-breakpoint';

type TreeNodeRowProps = {
  expanded: boolean;
  hasChildren: boolean;
  levelStyle: TreeLevelStyle;
  node: TreeNode;
  onToggleExpand: (node: TreeNode) => void;
  onToggleSelect: (node: TreeNode) => void;
  selected: Set<string>;
  trackingIdPrefix: string;
  variant: WidthVariant;
};

export const TreeNodeRow = ({
  expanded,
  hasChildren,
  levelStyle,
  node,
  onToggleExpand,
  onToggleSelect,
  selected,
  trackingIdPrefix,
  variant,
}: TreeNodeRowProps) => {
  const { t } = useTranslation('messages');
  const { checked, indeterminate } = getNodeState(node, selected);
  const indent = levelStyle.indent ?? theme.spacing(2);
  const showCount = (levelStyle.showCount ?? false) && node.count !== undefined;

  // Badge shows "selected of total": total is ALL of the group's locations (node.count, incl. disabled),
  // selected is how many selectable leaves are chosen — so a group with disabled children reads e.g. "6 of 8".
  const selectableLeafIds = hasChildren ? getSelectableLeafIds([node]) : [];
  const total = node.count ?? 0;
  const selectedCount = selectableLeafIds.filter((id) => selected.has(id)).length;
  const isAllSelected = total > 0 && selectedCount === total;
  const isPartiallySelected = selectedCount > 0 && !isAllSelected;

  const checkboxFieldProps = useControlledField({
    onChange: () => onToggleSelect(node),
    type: 'checkbox',
    // DS checkbox convention: `value` drives the fill + icon visibility, `indeterminate` only picks the
    // icon (dash vs check). So fill on partial OR full — otherwise a partially-selected group's dash is hidden.
    value: checked || indeterminate,
  });

  const countBadge = showCount ? (
    <span className={styles.count}>
      {selectedCount === 0 ? (
        <Text as='span' size='small' color='light'>
          {total}
        </Text>
      ) : variant === 'large' && isPartiallySelected ? (
        // One full phrase (not an "of N" fragment) so it translates cleanly; the pill wraps just the count.
        <Text as='span' size='small' color='light'>
          <Trans
            t={t}
            i18nKey='<pill>{{selectedCount}}</pill> of {{total}}'
            values={{ selectedCount, total }}
            components={{ pill: <span className={styles.countPill} /> }}
          />
        </Text>
      ) : (
        <span className={styles.countPill}>{selectedCount}</span>
      )}
    </span>
  ) : null;

  return (
    <div className={styles.row} style={{ paddingLeft: indent }}>
      {/* Left region selects the object (checkbox + label). */}
      <div className={styles.leading}>
        {levelStyle.showConnector && (
          <TreeConnector className={styles.connector} preserveAspectRatio='none' aria-hidden />
        )}
        <CheckboxField
          {...checkboxFieldProps}
          className={styles.checkbox}
          disabled={node.disabled}
          indeterminate={indeterminate}
          label={
            <span className={styles.label} style={{ color: levelStyle.textColor ?? theme.colorsV2.black(85) }}>
              {hasChildren && <Icon name='locations-small' size={16} color='subdued' className={styles.groupIcon} />}
              <span className={styles.labelText}>{node.label}</span>
            </span>
          }
          labelPlacement='right'
          name={`${trackingIdPrefix}-${node.id}`}
          trackingId={`${trackingIdPrefix}-option-${node.id}`}
        />
      </div>
      {/* Right region (count + caret) opens/closes the accordion for groups. */}
      {hasChildren ? (
        <button
          type='button'
          className={styles.expandTrigger}
          aria-label={expanded ? t('Collapse') : t('Expand')}
          aria-expanded={expanded}
          onClick={() => onToggleExpand(node)}
          data-trackingid={`${trackingIdPrefix}-caret-${node.id}`}
        >
          {countBadge}
          <Icon name={expanded ? 'caret-up-tiny' : 'caret-down-tiny'} size={16} color='subdued' />
        </button>
      ) : (
        <div className={styles.trailing}>{countBadge}</div>
      )}
    </div>
  );
};

const styles = {
  row: css`
    display: flex;
    align-items: center;
    padding: ${theme.spacing(1, 2)};
    border-bottom: 1px solid ${theme.colorsV2.black(10)};
    background: ${theme.colors.white};
  `,
  leading: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
    min-width: 0;
    flex: 0 1 auto;
  `,
  connector: css`
    width: 24px;
    height: 20px;
    margin-top: -8px;
    flex-shrink: 0;
  `,
  checkbox: css`
    margin: 0;
    min-width: 0;

    > label {
      min-width: 0;
    }
  `,
  label: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(0.5)};
    min-width: 0;
  `,
  groupIcon: css`
    flex-shrink: 0;
  `,
  labelText: css`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `,
  trailing: css`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: ${theme.spacing(0.5)};
    flex: 1;
    min-width: 0;
  `,
  count: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(0.5)};
    line-height: 20px;
  `,
  countPill: css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
    padding: ${theme.spacing(0, 0.5)};
    border-radius: ${theme.borderRadius.small};
    background: ${theme.colors.primary10};
    color: ${theme.colorsV2.black(85)};
    font-size: ${theme.font.size.small};
    font-weight: ${theme.font.weight.bold};
    line-height: 20px;
  `,
  expandTrigger: css`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: ${theme.spacing(0.5)};
    flex: 1 0 auto;
    align-self: stretch;
    border: none;
    background: none;
    cursor: pointer;
    padding: ${theme.spacing(0, 2)};
  `,
};
